import { useCallback, useEffect, useRef, useState } from 'react'
import type { Citation } from '@saip/backend'
import type { Scope } from '@saip/office'
import { useBackend, useHostAdapter } from './context'

export type ChatMode = 'fast' | 'thinking'

export interface ChatTurn {
  id: string
  role: 'user' | 'assistant'
  content: string
  reasoning?: string
  citations?: Citation[]
  streaming?: boolean
  /** Set when `content` is a user-facing error notice (e.g. backend unreachable). */
  error?: boolean
  /** e.g. "Auswahl · 1 Absatz" — shown above the user bubble. */
  scopeBadge?: string
}

export interface SendOptions {
  scope: Scope
  mode: ChatMode
  /** Optional RAG collection to ground the answer in. */
  collectionId?: string
  /** Picked prompt template body; its {{variables}} are filled with the in-scope text. */
  promptBody?: string
  /** Human label of the applied prompt, shown in the chat (e.g. "Kürzer fassen"). */
  promptLabel?: string
}

let counter = 0
const uid = () => `turn_${Date.now().toString(36)}_${counter++}`

/** Fill every {{variable}} in a prompt body with the in-scope text. */
const fillPrompt = (body: string, scoped: string): string =>
  body.replace(/\{\{\s*\w+\s*\}\}/g, scoped)

/**
 * Turn a caught backend/stream error into a human-facing German notice.
 * Returns null for a user-initiated cancel (stop button) — nothing to show.
 * Handles both BackendError instances (transport) and the {code,message} objects
 * carried in SSE `error` frames.
 */
function describeChatError(e: unknown): string | null {
  const err = (e ?? {}) as { code?: string; message?: string; name?: string }
  if (err.code === 'canceled' || err.name === 'AbortError') return null
  switch (err.code) {
    case 'network':
      return 'Keine Verbindung zum LiteLLM-Server. Prüfe die Server-Adresse in den Einstellungen und deine Netzwerkverbindung.'
    case 'timeout':
      return 'Zeitüberschreitung: Der LiteLLM-Server hat nicht rechtzeitig geantwortet. Bitte erneut versuchen.'
    case 'unauthorized':
      return 'Nicht autorisiert (401). Der Virtual Key ist ungültig oder abgelaufen — in den Einstellungen neu eintragen.'
    case 'forbidden':
      return 'Zugriff verweigert (403). Der Virtual Key ist für dieses Modell nicht freigegeben.'
    case 'not_found':
      return 'Modell oder Endpunkt nicht gefunden (404). Wähle in den Einstellungen ein verfügbares Modell.'
    case 'rate_limited':
      return 'Zu viele Anfragen (429). Bitte einen Moment warten und erneut versuchen.'
    default:
      // The server sends its own friendly text for e.g. "not_configured" — surface it.
      return err.message?.trim() || 'Es ist ein unbekannter Fehler aufgetreten.'
  }
}

export interface UseChat {
  turns: ChatTurn[]
  busy: boolean
  send: (text: string, opts: SendOptions) => Promise<void>
  stop: () => void
  reset: () => void
}

export function useChat(): UseChat {
  const backend = useBackend()
  const adapter = useHostAdapter()
  const [turns, setTurns] = useState<ChatTurn[]>([])
  const [busy, setBusy] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  // Body of the active system prompt — the assistant's persona, injected as the
  // leading system message on every turn. Loaded once; null until ready / if none.
  const systemPromptRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    backend.systemPrompts
      .getActive()
      .then((sp) => {
        if (!cancelled) systemPromptRef.current = sp?.body ?? null
      })
      .catch(() => {
        if (!cancelled) systemPromptRef.current = null
      })
    return () => {
      cancelled = true
    }
  }, [backend])

  const patch = useCallback((id: string, fn: (t: ChatTurn) => ChatTurn) => {
    setTurns((list) => list.map((t) => (t.id === id ? fn(t) : t)))
  }, [])

  const send = useCallback(
    async (text: string, opts: SendOptions) => {
      const trimmed = text.trim()
      if ((!trimmed && !opts.promptBody) || busy) return
      setBusy(true)
      const controller = new AbortController()
      abortRef.current = controller
      const asstId = uid()

      try {
        // Fetch the scoped context first — drives the badge, grounding and prompt vars.
        // Scope 'none' skips this entirely: a plain chat with no document/selection.
        let scoped = ''
        let scopeBadge: string | undefined
        if (opts.scope === 'selection') {
          const sel = await adapter.getSelection().catch(() => null)
          scoped = sel?.text ?? ''
          if (sel && !sel.isEmpty) {
            scopeBadge = `Auswahl · ${sel.paragraphs} ${sel.paragraphs === 1 ? 'Absatz' : 'Absätze'}`
          }
        } else if (opts.scope === 'document') {
          scoped = await adapter.getDocumentText().catch(() => '')
        }

        // A picked prompt resolves to the actual instruction: its {{variables}}
        // are filled with the in-scope text, plus any extra text the user typed.
        const hasPlaceholder = !!opts.promptBody && /\{\{\s*\w+\s*\}\}/.test(opts.promptBody)
        const instruction = opts.promptBody ? fillPrompt(opts.promptBody, scoped) : ''
        const sentContent = [instruction, trimmed].filter(Boolean).join('\n\n') || trimmed

        // The user's bubble shows their text or the prompt label — never the
        // full inlined document.
        const displayContent = trimmed || opts.promptLabel || sentContent
        if (opts.promptLabel && trimmed) {
          scopeBadge = [opts.promptLabel, scopeBadge].filter(Boolean).join(' · ')
        }

        const history = turns
        const userTurn: ChatTurn = { id: uid(), role: 'user', content: displayContent, scopeBadge }
        const asstTurn: ChatTurn = { id: asstId, role: 'assistant', content: '', streaming: true }
        setTurns((list) => [...list, userTurn, asstTurn])

        const messages = [
          // The active system prompt sets the assistant's persona for every turn.
          ...(systemPromptRef.current
            ? [{ role: 'system' as const, content: systemPromptRef.current }]
            : []),
          // Skip the context message for scope 'none', or when the prompt body already
          // inlines it.
          ...(hasPlaceholder || opts.scope === 'none'
            ? []
            : [{ role: 'system' as const, content: `Kontext (${opts.scope}):\n${scoped}` }]),
          ...history.map((t) => ({ role: t.role, content: t.content })),
          { role: 'user' as const, content: sentContent },
        ]

        const stream = backend.ai.stream(
          {
            messages,
            mode: opts.mode,
            rag: opts.collectionId ? { collectionId: opts.collectionId } : undefined,
          },
          { signal: controller.signal },
        )

        for await (const chunk of stream) {
          switch (chunk.type) {
            case 'delta':
              patch(asstId, (t) => ({ ...t, content: t.content + chunk.text }))
              break
            case 'reasoning':
              patch(asstId, (t) => ({ ...t, reasoning: (t.reasoning ?? '') + chunk.text }))
              break
            case 'citation':
              patch(asstId, (t) => ({ ...t, citations: [...(t.citations ?? []), chunk.citation] }))
              break
            case 'error':
              throw chunk.error
            default:
              break
          }
        }
      } catch (e) {
        const notice = describeChatError(e)
        if (notice) {
          patch(asstId, (t) => ({
            ...t,
            content: t.content ? `${t.content}\n\n${notice}` : notice,
            error: true,
          }))
        }
      } finally {
        patch(asstId, (t) => ({ ...t, streaming: false }))
        setBusy(false)
        abortRef.current = null
      }
    },
    [backend, adapter, turns, busy, patch],
  )

  const stop = useCallback(() => abortRef.current?.abort(), [])
  const reset = useCallback(() => setTurns([]), [])

  return { turns, busy, send, stop, reset }
}
